import React, { useState, useEffect, useCallback } from "react";
import { Search, Eye, Mail, Trash2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { listSurveys, resendSurvey, deleteSurvey } from "../../api";

const detailRows = (s) => [
  ["場域地點", s.location === "其他" ? s.locationOther : s.location],
  ["房間/帳篷數", s.roomCount], ["最多可住人數", s.maxGuests],
  ["目前住房率", s.occupancyRate], ["目前月營收", s.monthlyRevenue],
  ["是否投廣告", s.runningAds], ["網紅合作", s.kolCooperation],
  ["目標月營收", s.targetRevenue], ["平均單價", s.avgPrice],
  ["合作決定權", s.decisionMaker], ["到過人客多場域", s.visitedLakeze],
  ["稱呼", s.name], ["聯絡電話", s.phone], ["LINE 顯示名稱", s.lineDisplayName],
  ["LINE ID", s.lineId || "—"], ["場域 FB/IG", s.venueSocial || "—"], ["電子信箱", s.email],
];

export default function SurveysTable() {
  const [status, setStatus] = useState("all");
  const [location, setLocation] = useState("all");
  const [occupancy, setOccupancy] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ surveys: [], total: 0, pages: 1 });
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    listSurveys({ page, status, search, location, occupancy })
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, status, search, location, occupancy]);

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const doDelete = async (id) => {
    await deleteSurvey(id);
    toast.success("已刪除該筆問卷");
    load();
  };
  const resend = async (s) => {
    try {
      await resendSurvey(s.id);
      toast.success(`已重寄確認信至 ${s.email}`);
    } catch {
      toast.error("重寄失敗");
    }
  };

  const rows = data.surveys;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative flex-1 sm:min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="搜尋姓名/電話/LINE/場域" className="pl-9" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-32"><SelectValue placeholder="結果" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部結果</SelectItem>
            <SelectItem value="qualified">符合資格</SelectItem>
            <SelectItem value="rejected">不符合資格</SelectItem>
          </SelectContent>
        </Select>
        <Select value={location} onValueChange={(v) => { setLocation(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-32"><SelectValue placeholder="地點" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部地點</SelectItem>
            <SelectItem value="宜蘭縣">宜蘭縣</SelectItem>
            <SelectItem value="台中縣">台中縣</SelectItem>
            <SelectItem value="其他">其他</SelectItem>
          </SelectContent>
        </Select>
        <Select value={occupancy} onValueChange={(v) => { setOccupancy(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-36"><SelectValue placeholder="住房率" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部住房率</SelectItem>
            <SelectItem value="20% 以下">20% 以下</SelectItem>
            <SelectItem value="20-40%">20-40%</SelectItem>
            <SelectItem value="40-60%">40-60%</SelectItem>
            <SelectItem value="60-80%">60-80%</SelectItem>
            <SelectItem value="80% 以上">80% 以上</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-white shadow-sm">
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="bg-secondary text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-3">提交時間</th>
              <th className="px-3 py-3">用戶名稱</th>
              <th className="px-3 py-3">聯絡電話</th>
              <th className="px-3 py-3">LINE 顯示名稱</th>
              <th className="px-3 py-3">LINE ID</th>
              <th className="px-3 py-3">場域 FB/IG</th>
              <th className="px-3 py-3">電子信箱</th>
              <th className="px-3 py-3">場域地點</th>
              <th className="px-3 py-3">住房率</th>
              <th className="px-3 py-3">目標營收</th>
              <th className="px-3 py-3">結果</th>
              <th className="px-3 py-3 text-right">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && (
              <tr><td colSpan={11} className="px-3 py-10 text-center text-muted-foreground">載入中…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-10 text-center text-muted-foreground">尚無符合條件的問卷</td></tr>
            )}
            {!loading && rows.map((s) => (
              <tr key={s.id} className="hover:bg-orange-50/40">
                <td className="whitespace-nowrap px-3 py-3 text-xs text-muted-foreground">
                  {new Date(s.createdAt).toLocaleString("zh-TW", { hour12: false })}
                </td>
                <td className="px-3 py-3 font-medium">{s.name}</td>
                <td className="whitespace-nowrap px-3 py-3">{s.phone}</td>
                <td className="px-3 py-3">{s.lineDisplayName || "—"}</td>
                <td className="px-3 py-3">{s.lineId || "—"}</td>
                <td className="px-3 py-3">{s.venueSocial || "—"}</td>
                <td className="px-3 py-3">{s.email}</td>
                <td className="px-3 py-3">{s.location === "其他" ? s.locationOther : s.location}</td>
                <td className="px-3 py-3">{s.occupancyRate}</td>
                <td className="px-3 py-3">{s.targetRevenue}</td>
                <td className="px-3 py-3">
                  {s.status === "qualified" ? (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle2 className="mr-1 h-3 w-3" />符合</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-gray-500"><XCircle className="mr-1 h-3 w-3" />不符合</Badge>
                  )}
                </td>
                <td className="px-3 py-3">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDetail(s)}><Eye className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => resend(s)}><Mail className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => doDelete(s.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">共 {data.total} 筆 · 第 {page}/{data.pages} 頁</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>上一頁</Button>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>下一頁</Button>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>問卷詳情 — {detail?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3">
              {detail.status === "rejected" && detail.rejectionReasons && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                  不符合原因：{detail.rejectionReasons}
                </div>
              )}
              <dl className="divide-y divide-border text-sm">
                {detailRows(detail).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-2">
                    <dt className="shrink-0 text-muted-foreground">{k}</dt>
                    <dd className="text-right font-medium">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
